interface AnimateOptions {
  /** Tween length; 400ms default matches typical native smooth timing. */
  durationMs?: number
  /** Injectable for tests. */
  raf?: (cb: FrameRequestCallback) => number
  /** Injectable for tests. */
  now?: () => number
}

/**
 * Animated window scroll that works in EVERY environment.
 *
 * Native smooth scrolling — `scroll-behavior: smooth` and
 * `scrollIntoView({behavior:'smooth'})` — silently no-ops in browsers
 * where smooth scroll animation is disabled (observed live on the
 * station page: hash set, page never moved). rAF always runs, so the
 * anchor tabs drive this tween instead. Ease-in-out cubic; the final
 * frame lands on the exact target so anchors are never off by the
 * easing tail.
 *
 * Callers honour `prefers-reduced-motion` themselves by scrolling
 * instantly instead of calling this.
 */
export function animateWindowScrollTo(targetY: number, options: AnimateOptions = {}): void {
  const durationMs = options.durationMs ?? 400
  const raf = options.raf ?? ((cb) => window.requestAnimationFrame(cb))
  const now = options.now ?? (() => performance.now())

  const startY = window.scrollY
  const delta = targetY - startY
  const startedAt = now()

  const easeInOutCubic = (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

  const step = (): void => {
    const elapsed = now() - startedAt
    const t = Math.min(1, elapsed / durationMs)
    window.scrollTo({ top: startY + delta * easeInOutCubic(t), behavior: 'auto' })
    if (t < 1) raf(step)
  }
  raf(step)
}
